import { supabase } from '../lib/supabaseClient';
import { Entity } from '../types';

/**
 * Entity Service
 * Handles all operations related to entities (organizations/tenants)
 */

// Default theme values
const DEFAULT_THEME = {
  primaryColor: '#4A3AFF',
  secondaryColor: '#5C9DFF',
  logoUrl: undefined,
  headerBackgroundColor: '#1976d2',
  headerTextColor: '#ffffff'
};

// Default feature flags
const DEFAULT_FEATURES = {
  resumeScreening: true,
  aiScreening: true,
  analytics: true,
  mockInterviews: true,
  callInitiation: true,
  customThemes: true
};

// Map database entity to Entity type
const mapDbEntityToEntity = (data: any): Entity => {
  return {
    id: data.id,
    name: data.name,
    subdomain: data.subdomain,
    logo: data.logo,
    theme: {
      primaryColor: data.theme?.primaryColor || DEFAULT_THEME.primaryColor,
      secondaryColor: data.theme?.secondaryColor || DEFAULT_THEME.secondaryColor,
      logoUrl: data.theme?.logoUrl || data.logo,
      headerBackgroundColor: data.theme?.headerBackgroundColor || DEFAULT_THEME.headerBackgroundColor,
      headerTextColor: data.theme?.headerTextColor || DEFAULT_THEME.headerTextColor
    },
    features: {
      resumeScreening: data.features?.resumeScreening ?? DEFAULT_FEATURES.resumeScreening,
      aiScreening: data.features?.aiScreening ?? DEFAULT_FEATURES.aiScreening,
      analytics: data.features?.analytics ?? DEFAULT_FEATURES.analytics,
      mockInterviews: data.features?.mockInterviews ?? DEFAULT_FEATURES.mockInterviews,
      callInitiation: data.features?.callInitiation ?? DEFAULT_FEATURES.callInitiation,
      customThemes: data.features?.customThemes ?? DEFAULT_FEATURES.customThemes
    }
  };
};

// Get entity by subdomain
export const getEntityBySubdomain = async (
  subdomain: string
): Promise<Entity | null> => {
  try {
    const { data, error } = await supabase
      .from('entities')
      .select('*')
      .eq('subdomain', subdomain)
      .eq('is_active', true)
      .maybeSingle();

    if (error) {
      console.error('Error fetching entity by subdomain:', error);
      return null;
    }

    if (!data) {
      console.log(`No entity found for subdomain: ${subdomain}`);
      return null;
    }

    return mapDbEntityToEntity(data);
  } catch (error) {
    console.error('Error in getEntityBySubdomain:', error);
    return null;
  }
};

// Get entity by ID
export const getEntityById = async (id: string): Promise<Entity | null> => {
  try {
    const { data, error } = await supabase
      .from('entities')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching entity by ID:', error);
      return null;
    }

    return mapDbEntityToEntity(data);
  } catch (error) {
    console.error('Error in getEntityById:', error);
    return null;
  }
};

// Get all active entities
export const getAllEntities = async (): Promise<Entity[]> => {
  try {
    const { data, error } = await supabase
      .from('entities')
      .select('*')
      .eq('is_active', true)
      .order('name');

    if (error) {
      console.error('Error fetching entities:', error);
      return [];
    }

    return data as Entity[];
  } catch (error) {
    console.error('Error in getAllEntities:', error);
    return [];
  }
};

// Update entity
export const updateEntity = async (
  id: string,
  updates: Partial<Entity>
): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('entities')
      .update(updates)
      .eq('id', id);

    if (error) {
      console.error('Error updating entity:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in updateEntity:', error);
    return false;
  }
};

// Create entity (admin only)
export const createEntity = async (
  entity: Omit<Entity, 'id'>
): Promise<Entity | null> => {
  try {
    const { data, error } = await supabase
      .from('entities')
      .insert([entity])
      .select()
      .single();

    if (error) {
      console.error('Error creating entity:', error);
      return null;
    }

    return data as Entity;
  } catch (error) {
    console.error('Error in createEntity:', error);
    return null;
  }
};
